import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetLogsQuery, useGetLogByIdQuery } from "../../../services/auditLogsAPI";
import FilterBar from "./components/FilterBar";
import AuditLogTable from "./components/AuditLogTable";
import LogDetailsPanel from "./components/LogDetailsPanel";
import DownloadPanel from "./components/DownloadPanel";
import { FilterState, PaginationState, AuditLog } from "./types";
import { initialFilters, initialPaginationState } from "./utils";
import Skeleton from "react-loading-skeleton";
import { useAppSelector } from "../../../app/hooks";
import { selectGroupById } from "../../../features/groups/groupSlice";
import { MdDownload, MdRefresh } from "react-icons/md";
import useGetLanguage from "../../../utils/useGetLanguage";
import { LanguageText } from "../../../dictionary/GroupsText";

const AuditLogs: React.FC = () => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });
  const { groupid } = useParams<{ groupid: string }>();
  const groupInfo = useAppSelector((state) => selectGroupById(state, groupid ?? ""));
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [pagination, setPagination] = useState<PaginationState>(initialPaginationState);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isDownloadPanelOpen, setIsDownloadPanelOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(filters);

  const {
    data: logsData,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetLogsQuery(groupid ?? "", {
    skip: !groupid,
  });

  // Extract unique user IDs for the UserFilter component
  const uniqueUserIds = useMemo(() => {
    if (!logsData || !Array.isArray(logsData)) return [];
    return Array.from(new Set(logsData.map((log) => log.user_id)));
  }, [logsData]);

  // Filtered and paginated logs - sorted by timestamp (earliest first)
  const filteredLogs = useMemo(() => {
    if (!logsData || !Array.isArray(logsData)) {
      return [];
    }

    // Create a copy to avoid mutating the original array
    let filtered = [...logsData];

    // Apply user filter if selected
    if (selectedUserId) {
      // Filter logs to only show logs from the selected user
      filtered = filtered.filter((log) => log.user_id === selectedUserId);
    }

    // Apply basic filters that don't require user info
    if (appliedFilters.resourceType) {
      filtered = filtered.filter(
        (log) => log.resource_type === appliedFilters.resourceType
      );
    }

    if (appliedFilters.action) {
      filtered = filtered.filter((log) => log.action === appliedFilters.action);
    }

    if (appliedFilters.dateRange.start && appliedFilters.dateRange.end) {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.timestamp);
        return (
          logDate >= appliedFilters.dateRange.start! &&
          logDate <= appliedFilters.dateRange.end!
        );
      });
    }

    // Sort by timestamp (earliest first) - create a copy before sorting
    const sorted = [...filtered].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    return sorted;
  }, [logsData, appliedFilters, selectedUserId]);

  // Update pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      totalItems: filteredLogs.length,
    }));
  }, [filteredLogs.length]);

  // Handlers
  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setSelectedUserId(""); // Clear selected user ID
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePaginationChange = (newPagination: PaginationState) => {
    setPagination(newPagination);
  };

  const handleShowDetails = (log: AuditLog) => {
    setSelectedLog(log);
  };

  const handleCloseDetails = () => {
    setSelectedLog(null);
  };

  const handleDownloadClick = () => {
    setIsDownloadPanelOpen(true);
  };

  const handleCloseDownload = () => {
    setIsDownloadPanelOpen(false);
  };

  // Handle user selection from UserFilter
  const handleUserSelect = (userId: string, userEmail: string) => {
    setSelectedUserId(userId);
    // Update the filters to show the selected user email
    setFilters((prev) => ({ ...prev, user: userEmail }));
  };

  // Error and loading states
  if (!groupid) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-gray-500 text-lg">Please select a group to view logs.</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full h-full bg-gray-50">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 lg:p-6">
          <div>
            <Skeleton height={32} width={200} className="mb-2" />
            <Skeleton height={16} width={300} />
          </div>
          <div className="mt-4 sm:mt-0">
            <Skeleton height={40} width={120} />
          </div>
        </div>

        {/* Filters Skeleton */}
        <div className="px-4 lg:px-6 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <Skeleton height={16} width={80} className="mb-1" />
                  <Skeleton height={40} width="100%" />
                </div>
              ))}
            </div>
            <div className="flex justify-end items-center space-x-3">
              <Skeleton height={32} width={60} />
              <Skeleton height={32} width={100} />
            </div>
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="px-4 lg:px-6 w-full">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <Skeleton height={24} width={120} />
                <div className="flex items-center space-x-4">
                  <Skeleton height={16} width={40} />
                  <Skeleton height={32} width={60} />
                  <Skeleton height={16} width={60} />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <Skeleton height={16} width={80} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((row) => (
                    <tr key={row}>
                      {[1, 2, 3, 4, 5, 6].map((cell) => (
                        <td key={cell} className="px-6 py-4">
                          <Skeleton height={16} width={100} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Error loading logs</div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-50">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 lg:p-6">
        <div className="mt-4 sm:mt-0 flex items-center space-x-3 ml-auto">
          {isFetching ? (
            <div className="flex items-center text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              {Language?.groupAuditLogs?.refreshing}...
            </div>
          ) : (
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isFetching}
            >
              <MdRefresh className="w-4 h-4 mr-2" /> {Language?.groupAuditLogs?.refresh}
            </button>
          )}

          <button
            onClick={handleDownloadClick}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <MdDownload className="w-4 h-4 mr-2" /> {Language?.groupAuditLogs?.download}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="px-4 lg:px-6 mb-6">
        <FilterBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          groupId={groupid}
          uniqueUserIds={uniqueUserIds}
          onUserSelect={handleUserSelect}
        />
      </div>

      {/* Audit Log Table */}
      <div className="px-4 lg:px-6 w-full">
        <AuditLogTable
          logs={filteredLogs}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          onShowDetails={handleShowDetails}
          groupId={groupid}
          ownerID={groupInfo?.owner_id}
        />
      </div>

      {/* Log Details Panel */}
      {selectedLog && (
        <LogDetailsPanel
          log={selectedLog}
          groupId={groupid}
          onClose={handleCloseDetails}
        />
      )}

      {/* Download Panel */}
      <DownloadPanel
        isOpen={isDownloadPanelOpen}
        onClose={handleCloseDownload}
        groupId={groupid}
        logs={filteredLogs}
      />
    </div>
  );
};

export default AuditLogs;
